import { Icon } from '@iconify/react'
import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

interface AlertProps {
    /** Error message to display */
    apiError: string
    /** Function to update the API error state */
    setApiError: React.Dispatch<React.SetStateAction<string>>
    /** Disables auto-closing of the alert if set to true */
    disableAutoClose?: boolean
    /** Indicates whether an error was encountered */
    errorEncountered?: boolean
}
interface AlertProps {
    apiError: string
    setApiError: React.Dispatch<React.SetStateAction<string>>
    disableAutoClose?: boolean
    errorEncountered?: boolean
}

/**
 * Alert component to display API error messages.
 * It auto-closes after 3 seconds unless `disableAutoClose` is set to true.
 *
 * @component
 * @param {AlertProps} props - The properties passed to the component
 * @returns {JSX.Element} A styled alert message component
 */
const Alert: React.FC<AlertProps> = (props) => {
    const navigate = useNavigate()
    useEffect(() => {
        // Only set the timeout if auto close is not disabled
        if (!props.disableAutoClose) {
            const timeoutId = setTimeout(() => props.setApiError(''), 3000)

            return () => {
                clearTimeout(timeoutId)
            }
        }
        // eslint-disable-next-line
    }, [props.disableAutoClose])
    /**
     * Formats the error message by splitting it into multiple lines if necessary.
     *
     * @param {string} message - The error message to format
     * @returns {JSX.Element | string} Formatted message with line breaks
     */
    const formatErrorMessage = (message: string) => {
        if (message.includes('\n')) {
            const lines = message.split('\n')
            return lines.map((line, index) => (
                <React.Fragment key={index}>
                    {line}
                    {index !== lines.length - 1 && (
                        <>
                            <br />
                            <br />
                        </>
                    )}
                </React.Fragment>
            ))
        } else {
            return message
        }
    }

    return (
        <div className='p-4 mb-2 bg-red-100 border-red-900 rounded' role='alert'>
            <div className='flex items-center justify-between gap-4'>
                <p className='m-0 mr-4 text-sm font-medium text-left text-red-900 md:w-11/12'>
                    {formatErrorMessage(props.apiError) || 'Something went wrong!'}
                </p>
                <Icon
                    icon='system-uicons:cross-circle'
                    className='text-2xl text-red-900 cursor-pointer'
                    onClick={() => {
                        props.setApiError('')
                        if (props.errorEncountered) {
                            navigate('/auth/sign-up')
                        }
                    }}
                />
            </div>
        </div>
    )
}

export default Alert
